// frontend/src/pages/staff/MyLooksPage.js
// Provider Looks management library — Phase 2.
// Routes: /dashboard/professional/looks, /dashboard/freelancer/looks, /dashboard/booth-renter/looks

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import StaffSidebarLayout from "./StaffSidebarLayout";
import FreelancerSidebarLayout from "../freelancer/FreelancerSidebarLayout";
import {
  FaImage,
  FaEye,
  FaArchive,
  FaTrash,
  FaEnvelope,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaRedo,
  FaCheckSquare,
  FaSquare,
  FaBolt,
  FaGavel,
  FaTimes,
} from "react-icons/fa";

const STATUS_LABELS = {
  draft: { label: "Draft", color: "#888" },
  awaiting_consent: { label: "Awaiting Consent", color: "#f0a500" },
  ready: { label: "Ready", color: "#3b82f6" },
  published: { label: "Published", color: "#22c55e" },
  archived: { label: "Archived", color: "#6b7280" },
  hidden_by_admin: { label: "Hidden by Admin", color: "#dc2626" },
  reported: { label: "Reported", color: "#ef4444" },
  sanctioned: { label: "Sanctioned", color: "#b45309" },
  under_review: { label: "Under Review", color: "#7c3aed" },
  escalated: { label: "Escalated", color: "#7c2d12" },
};

const CONSENT_LABELS = {
  pending: { label: "Pending", icon: <FaClock style={{ color: "#f0a500" }} /> },
  approved: {
    label: "Approved",
    icon: <FaCheckCircle style={{ color: "#22c55e" }} />,
  },
  declined: {
    label: "Declined",
    icon: <FaTimesCircle style={{ color: "#ef4444" }} />,
  },
  revoked: {
    label: "Revoked",
    icon: <FaTimesCircle style={{ color: "#ef4444" }} />,
  },
};

const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "awaiting_consent", label: "Awaiting Consent" },
  { key: "published", label: "Published" },
  { key: "archived", label: "Archived" },
  { key: "reported", label: "Reported" },
  { key: "sanctioned", label: "Sanctioned" },
];

const APPEAL_STATUS_COLORS = {
  pending: { label: "Appeal Pending", color: "#f0a500", bg: "#fffbeb" },
  approved: { label: "Appeal Approved", color: "#22c55e", bg: "#f0fdf4" },
  denied: { label: "Appeal Denied", color: "#ef4444", bg: "#fee2e2" },
};

function isSanctionedLook(look) {
  return look.auditTrail?.some((e) =>
    [
      "admin_provider_warn",
      "admin_provider_restrict",
      "admin_provider_suspend",
    ].includes(e.event),
  );
}

function getModerationBadge(look) {
  if (look.reporting?.isHiddenPendingReview) {
    return STATUS_LABELS.hidden_by_admin;
  }

  if (isSanctionedLook(look)) {
    return STATUS_LABELS.sanctioned;
  }

  if (look.moderation?.queueStatus === "escalated") {
    return STATUS_LABELS.escalated;
  }

  if (look.moderation?.queueStatus === "under_review") {
    return STATUS_LABELS.under_review;
  }

  if (look.reporting?.isReported) {
    return STATUS_LABELS.reported;
  }

  return null;
}

const BULK_ACTIONS = [
  { key: "archive", label: "Archive Selected" },
  { key: "unarchive", label: "Unarchive Selected" },
  { key: "request-consent", label: "Request Consent" },
  { key: "delete", label: "Delete Selected" },
];

function MyLooksPage({ roleType = "professional" }) {
  const navigate = useNavigate();

  // R3 professionals use the shop (Staff) layout; freelancers (R4) and booth
  // renters (R7) use the freelancer layout so the full menu stays visible.
  const Layout =
    roleType === "professional" ? StaffSidebarLayout : FreelancerSidebarLayout;

  const [looks, setLooks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterTab, setFilterTab] = useState("all");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selectedLook, setSelectedLook] = useState(null);

  // Bulk select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // { success, skipped, failed }

  // Appeals
  const [myAppeals, setMyAppeals] = useState([]);
  const [appealsLoading, setAppealsLoading] = useState(false);
  const [showAppeals, setShowAppeals] = useState(false);
  const [appealModal, setAppealModal] = useState(null); // lookId
  const [appealReason, setAppealReason] = useState("");
  const [appealBusy, setAppealBusy] = useState(false);
  const [appealError, setAppealError] = useState("");

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = useCallback(() => {
    return process.env.REACT_APP_API_BASE_URL || "";
  }, []);

  const loadLooks = useCallback(async () => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;

    try {
      setLoading(true);
      setError("");
      const [looksRes, statsRes] = await Promise.all([
        fetch(`${baseURL}/api/looks/mine`, { headers }),
        fetch(`${baseURL}/api/looks/provider/stats`, { headers }),
      ]);
      const looksData = await looksRes.json();
      const statsData = await statsRes.json();
      if (!looksRes.ok)
        throw new Error(looksData?.message || "Failed to load looks");
      setLooks(looksData.looks || []);
      if (statsRes.ok) setStats(statsData.stats || null);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message || "Failed to load looks");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  useEffect(() => {
    loadLooks();
  }, [loadLooks]);

  const archiveLook = useCallback(
    async (lookId, archive) => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      setBusyId(lookId);
      try {
        const res = await fetch(`${baseURL}/api/looks/${lookId}/archive`, {
          method: "PATCH",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ archive }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed");
        setLooks((prev) =>
          prev.map((l) =>
            l._id === lookId ? { ...l, status: data.status } : l,
          ),
        );
      } catch (err) {
        alert(err.message);
      } finally {
        setBusyId(null);
      }
    },
    [getAuthHeaders, getApiBaseUrl],
  );

  const deleteLook = useCallback(
    async (lookId) => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      setBusyId(lookId);
      try {
        const res = await fetch(`${baseURL}/api/looks/${lookId}`, {
          method: "DELETE",
          headers,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to delete");
        setLooks((prev) => prev.filter((l) => l._id !== lookId));
        setConfirmDelete(null);
        if (selectedLook?._id === lookId) setSelectedLook(null);
      } catch (err) {
        alert(err.message);
      } finally {
        setBusyId(null);
      }
    },
    [getAuthHeaders, getApiBaseUrl, selectedLook],
  );

  const requestConsent = useCallback(
    async (lookId) => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      setBusyId(lookId);
      try {
        const res = await fetch(
          `${baseURL}/api/looks/${lookId}/request-consent`,
          {
            method: "POST",
            headers,
          },
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to send");
        await loadLooks();
        if (data.emailDelivered === false) {
          alert(
            "Consent requested, but the consent email could not be sent. Check the customer's email and try again.",
          );
        } else {
          alert("Consent request sent.");
        }
      } catch (err) {
        alert(err.message);
      } finally {
        setBusyId(null);
      }
    },
    [getAuthHeaders, getApiBaseUrl, loadLooks],
  );

  const openDetail = useCallback(
    async (lookId) => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      try {
        const res = await fetch(`${baseURL}/api/looks/${lookId}`, { headers });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed");
        setSelectedLook(data.look);
      } catch (err) {
        alert(err.message);
      }
    },
    [getAuthHeaders, getApiBaseUrl],
  );

  // Set which photo is the look's main/cover (index 0 drives the whole UI).
  const makeMainImage = useCallback(
    async (lookId, index) => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      setBusyId(lookId);
      try {
        const res = await fetch(`${baseURL}/api/looks/${lookId}/main-image`, {
          method: "PATCH",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ index }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed");
        const moveToFront = (imgs) => {
          if (index <= 0 || index >= imgs.length) return imgs;
          const next = [...imgs];
          const [chosen] = next.splice(index, 1);
          next.unshift(chosen);
          return next;
        };
        setSelectedLook((prev) =>
          prev && Array.isArray(prev.images)
            ? { ...prev, images: moveToFront(prev.images) }
            : prev,
        );
        setLooks((prev) =>
          prev.map((l) =>
            l._id === lookId && Array.isArray(l.images)
              ? { ...l, images: moveToFront(l.images) }
              : l,
          ),
        );
      } catch (err) {
        alert(err.message);
      } finally {
        setBusyId(null);
      }
    },
    [getAuthHeaders, getApiBaseUrl],
  );

  const loadMyAppeals = useCallback(async () => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;
    setAppealsLoading(true);
    try {
      const res = await fetch(`${baseURL}/api/looks/mine/appeals`, { headers });
      const data = await res.json();
      if (res.ok) setMyAppeals(data.looks || []);
    } catch (_) {
    } finally {
      setAppealsLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const submitAppeal = useCallback(async () => {
    if (!appealModal || !appealReason.trim()) return;
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;
    setAppealBusy(true);
    setAppealError("");
    try {
      const res = await fetch(`${baseURL}/api/looks/${appealModal}/appeal`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ reason: appealReason.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to submit appeal");
      setAppealModal(null);
      setAppealReason("");
      await loadLooks();
      await loadMyAppeals();
    } catch (err) {
      setAppealError(err.message || "Failed to submit appeal");
    } finally {
      setAppealBusy(false);
    }
  }, [
    appealModal,
    appealReason,
    getAuthHeaders,
    getApiBaseUrl,
    loadLooks,
    loadMyAppeals,
  ]);

  const applyBulkAction = useCallback(async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;

    setBulkBusy(true);
    setBulkResult(null);
    try {
      const res = await fetch(`${baseURL}/api/looks/bulk`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          action: bulkAction,
          lookIds: Array.from(selectedIds),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Bulk action failed");
      setBulkResult(data.results);
      await loadLooks();
      setBulkAction("");
    } catch (err) {
      alert(err.message);
    } finally {
      setBulkBusy(false);
    }
  }, [bulkAction, selectedIds, getAuthHeaders, getApiBaseUrl, loadLooks]);

  // Filtering
  const filteredLooks = looks.filter((l) => {
    const tabMatch =
      filterTab === "all" ||
      (filterTab === "reported"
        ? l.reporting?.isReported
        : filterTab === "sanctioned"
          ? isSanctionedLook(l)
          : l.status === filterTab);
    const q = search.toLowerCase();
    const searchMatch =
      !q ||
      (l.bookingSnapshot?.serviceName || "").toLowerCase().includes(q) ||
      (l.bookingSnapshot?.appointmentDate || "").toLowerCase().includes(q) ||
      (l._id || "").toLowerCase().includes(q) ||
      (l.recordId || "").toLowerCase().includes(q);
    return tabMatch && searchMatch;
  });

  const allVisibleSelected =
    filteredLooks.length > 0 &&
    filteredLooks.every((l) => selectedIds.has(l._id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredLooks.map((l) => l._id)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const coverImage = (l) => l.images?.[0]?.url || l.image?.url;

  const s = {
    page: { padding: "24px" },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "24px",
    },
    title: { fontSize: "22px", fontWeight: "700", margin: 0 },
    statsRow: {
      display: "flex",
      gap: "16px",
      flexWrap: "wrap",
      marginBottom: "24px",
    },
    statCard: {
      background: "#f9f9f9",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      padding: "14px 20px",
      minWidth: "120px",
      textAlign: "center",
    },
    statNum: { fontSize: "26px", fontWeight: "700", color: "#1a1a1a" },
    statLabel: { fontSize: "12px", color: "#6b7280", marginTop: "2px" },
    filterBar: {
      display: "flex",
      gap: "8px",
      flexWrap: "wrap",
      marginBottom: "12px",
    },
    filterTab: (active) => ({
      padding: "6px 14px",
      borderRadius: "20px",
      border: "1px solid",
      borderColor: active ? "#1a1a1a" : "#d1d5db",
      background: active ? "#1a1a1a" : "#fff",
      color: active ? "#fff" : "#374151",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: active ? "600" : "400",
    }),
    toolbar: {
      display: "flex",
      gap: "10px",
      alignItems: "center",
      flexWrap: "wrap",
      marginBottom: "16px",
    },
    searchInput: {
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "14px",
      width: "240px",
    },
    select: {
      padding: "7px 10px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "13px",
    },
    btn: (variant = "default") => ({
      padding: "7px 12px",
      borderRadius: "6px",
      border: "1px solid",
      fontSize: "12px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "5px",
      background:
        variant === "danger"
          ? "#fee2e2"
          : variant === "primary"
            ? "#1a1a1a"
            : "#f9fafb",
      color:
        variant === "danger"
          ? "#b91c1c"
          : variant === "primary"
            ? "#fff"
            : "#374151",
      borderColor:
        variant === "danger"
          ? "#fca5a5"
          : variant === "primary"
            ? "#1a1a1a"
            : "#d1d5db",
    }),
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
      gap: "16px",
    },
    card: (selected) => ({
      background: "#fff",
      border: `1px solid ${selected ? "#1a1a1a" : "#e5e7eb"}`,
      borderRadius: "10px",
      overflow: "hidden",
      boxShadow: selected
        ? "0 0 0 2px #1a1a1a22"
        : "0 1px 3px rgba(0,0,0,0.06)",
    }),
    cardBody: { padding: "12px" },
    statusBadge: (status) => ({
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: "600",
      background: (STATUS_LABELS[status]?.color || "#888") + "22",
      color: STATUS_LABELS[status]?.color || "#374151",
      marginBottom: "6px",
    }),
    actions: {
      display: "flex",
      gap: "6px",
      flexWrap: "wrap",
      marginTop: "10px",
    },
    empty: { textAlign: "center", padding: "48px", color: "#9ca3af" },
    checkbox: {
      cursor: "pointer",
      width: "16px",
      height: "16px",
      accentColor: "#1a1a1a",
    },
    modal: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.5)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    modalBox: {
      background: "#fff",
      borderRadius: "10px",
      padding: "28px",
      maxWidth: "480px",
      width: "90%",
    },
    detailModal: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.6)",
      zIndex: 1000,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      overflowY: "auto",
      padding: "40px 16px",
    },
    detailBox: {
      background: "#fff",
      borderRadius: "10px",
      padding: "24px",
      maxWidth: "600px",
      width: "100%",
    },
  };

  return (
    <Layout>
      <div style={s.page}>
        <div style={s.header}>
          <h1 style={s.title}>My Looks</h1>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              style={s.btn()}
              onClick={() => {
                setShowAppeals((v) => {
                  if (!v) loadMyAppeals();
                  return !v;
                });
              }}
            >
              <FaGavel size={11} /> My Appeals
            </button>
            <button
              style={s.btn("primary")}
              onClick={loadLooks}
              disabled={loading}
            >
              <FaRedo /> Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div style={s.statsRow}>
            {[
              { num: stats.total, label: "Total Looks" },
              { num: stats.published, label: "Published" },
              { num: stats.archived, label: "Archived" },
              { num: `${stats.approvalRate}%`, label: "Approval Rate" },
              { num: `${stats.publishRate}%`, label: "Publish Rate" },
              {
                num: `${stats.reportRate}%`,
                label: "Report Rate",
                danger: stats.reportRate > 0,
              },
            ].map(({ num, label, danger }) => (
              <div key={label} style={s.statCard}>
                <div
                  style={{
                    ...s.statNum,
                    color: danger ? "#ef4444" : "#1a1a1a",
                  }}
                >
                  {num}
                </div>
                <div style={s.statLabel}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filter Tabs */}
        <div style={s.filterBar}>
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              style={s.filterTab(filterTab === tab.key)}
              onClick={() => setFilterTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Toolbar: search + bulk */}
        <div style={s.toolbar}>
          <input
            style={s.searchInput}
            placeholder="Search by service, date, look ID, record ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* Select all toggle */}
          {filteredLooks.length > 0 && (
            <button
              style={s.btn()}
              onClick={toggleSelectAll}
              title={allVisibleSelected ? "Deselect all" : "Select all"}
            >
              {allVisibleSelected ? (
                <FaCheckSquare size={13} />
              ) : (
                <FaSquare size={13} />
              )}
              {selectedIds.size > 0
                ? `${selectedIds.size} selected`
                : "Select all"}
            </button>
          )}

          {/* Bulk action controls */}
          {selectedIds.size > 0 && (
            <>
              <select
                style={s.select}
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
              >
                <option value="">— Bulk action —</option>
                {BULK_ACTIONS.map((a) => (
                  <option key={a.key} value={a.key}>
                    {a.label}
                  </option>
                ))}
              </select>
              <button
                style={s.btn(bulkAction === "delete" ? "danger" : "primary")}
                disabled={!bulkAction || bulkBusy}
                onClick={applyBulkAction}
              >
                <FaBolt size={11} />
                {bulkBusy ? "Applying…" : "Apply"}
              </button>
              <button style={s.btn()} onClick={() => setSelectedIds(new Set())}>
                Clear
              </button>
            </>
          )}
        </div>

        {/* Bulk result feedback */}
        {bulkResult && (
          <div
            style={{
              marginBottom: "14px",
              padding: "10px 14px",
              borderRadius: "8px",
              background: "#f0fdf4",
              border: "1px solid #86efac",
              fontSize: "13px",
              color: "#166534",
            }}
          >
            Done — {bulkResult.success?.length || 0} updated
            {bulkResult.skipped?.length > 0
              ? `, ${bulkResult.skipped.length} skipped`
              : ""}
            {bulkResult.failed?.length > 0
              ? `, ${bulkResult.failed.length} failed`
              : ""}
            .
            <button
              style={{
                marginLeft: "10px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#166534",
                fontSize: "12px",
              }}
              onClick={() => setBulkResult(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        {loading && <div style={s.empty}>Loading looks…</div>}
        {error && <div style={{ ...s.empty, color: "#ef4444" }}>{error}</div>}
        {!loading && !error && filteredLooks.length === 0 && (
          <div style={s.empty}>
            <FaImage size={32} style={{ marginBottom: "12px" }} />
            <div>No looks found</div>
            {filterTab !== "all" && (
              <div style={{ fontSize: "13px", marginTop: "6px" }}>
                Try switching to "All"
              </div>
            )}
          </div>
        )}

        {!loading && !error && filteredLooks.length > 0 && (
          <div style={s.grid}>
            {filteredLooks.map((look) => {
              const img = coverImage(look);
              const isSelected = selectedIds.has(look._id);
              const isBusy = busyId === look._id;
              const consentInfo =
                CONSENT_LABELS[look.consent?.status] || CONSENT_LABELS.pending;
              const moderationBadge = getModerationBadge(look);

              return (
                <div key={look._id} style={s.card(isSelected)}>
                  {/* Thumbnail with checkbox overlay */}
                  <div style={{ position: "relative" }}>
                    {img ? (
                      <img
                        src={img}
                        alt="Look"
                        style={{
                          width: "100%",
                          height: "160px",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "160px",
                          background: "#f3f4f6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#9ca3af",
                        }}
                      >
                        <FaImage size={28} />
                      </div>
                    )}
                    <div
                      style={{
                        position: "absolute",
                        top: "8px",
                        left: "8px",
                        cursor: "pointer",
                      }}
                      onClick={() => toggleSelect(look._id)}
                    >
                      {isSelected ? (
                        <FaCheckSquare
                          size={18}
                          color="#1a1a1a"
                          style={{
                            filter:
                              "drop-shadow(0 1px 2px rgba(255,255,255,0.8))",
                          }}
                        />
                      ) : (
                        <FaSquare
                          size={18}
                          color="#fff"
                          style={{
                            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))",
                          }}
                        />
                      )}
                    </div>
                    {(look.images?.length || 0) > 1 && (
                      <div
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          background: "rgba(0,0,0,0.55)",
                          color: "#fff",
                          borderRadius: "10px",
                          padding: "2px 7px",
                          fontSize: "11px",
                        }}
                      >
                        {look.images.length} imgs
                      </div>
                    )}
                  </div>

                  <div style={s.cardBody}>
                    <div style={s.statusBadge(look.status)}>
                      {STATUS_LABELS[look.status]?.label || look.status}
                    </div>
                    {moderationBadge && (
                      <div
                        style={{
                          ...s.statusBadge("reported"),
                          marginLeft: "6px",
                          background: `${moderationBadge.color}22`,
                          color: moderationBadge.color,
                        }}
                      >
                        <FaExclamationTriangle size={10} style={{ marginRight: "4px" }} />
                        {moderationBadge.label}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        marginBottom: "2px",
                      }}
                    >
                      {look.bookingSnapshot?.serviceName || "Service"}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      {look.bookingSnapshot?.appointmentDate || ""}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        fontSize: "12px",
                        color: "#6b7280",
                        marginTop: "5px",
                      }}
                    >
                      {consentInfo.icon} Consent: {consentInfo.label}
                    </div>
                      {look.consent?.approvedAt && (
                        <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "3px" }}>
                          Consent approved: {new Date(look.consent.approvedAt).toLocaleString()}
                        </div>
                      )}
                      <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "3px" }}>
                        Record ID: {look.recordId || look._id}
                      </div>
                      <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>
                        Unique views: {(look.analytics?.viewCount || 0).toLocaleString()} · Clicks: {(look.analytics?.clickCount || 0).toLocaleString()} · Ratio: {look.analytics?.viewCount > 0 ? `${((look.analytics?.clickCount || 0) / look.analytics.viewCount).toFixed(1)}x` : "1.0x"}
                      </div>

                    {/* Appeal status badge */}
                    {look.appeal?.status &&
                      look.appeal.status !== "none" &&
                      look.appeal.status !== "withdrawn" &&
                      APPEAL_STATUS_COLORS[look.appeal.status] && (
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: "600",
                            background:
                              APPEAL_STATUS_COLORS[look.appeal.status].bg,
                            color:
                              APPEAL_STATUS_COLORS[look.appeal.status].color,
                            marginTop: "4px",
                            marginBottom: "4px",
                          }}
                        >
                          <FaGavel size={9} />{" "}
                          {APPEAL_STATUS_COLORS[look.appeal.status].label}
                        </div>
                      )}

                    <div style={s.actions}>
                      <button
                        style={s.btn()}
                        onClick={() => openDetail(look._id)}
                        title="View details"
                      >
                        <FaEye size={11} /> View
                      </button>
                      {(look.consent?.status === "pending" ||
                        look.consent?.status === "declined") &&
                        look.status !== "archived" && (
                          <button
                            style={s.btn()}
                            onClick={() => requestConsent(look._id)}
                            disabled={isBusy}
                            title="Send consent"
                          >
                            <FaEnvelope size={11} />
                          </button>
                        )}
                      {look.status !== "archived" ? (
                        <button
                          style={s.btn()}
                          onClick={() => archiveLook(look._id, true)}
                          disabled={isBusy}
                          title="Archive"
                        >
                          <FaArchive size={11} />
                        </button>
                      ) : (
                        <button
                          style={s.btn()}
                          onClick={() => archiveLook(look._id, false)}
                          disabled={isBusy}
                          title="Unarchive"
                        >
                          <FaArchive size={11} />
                        </button>
                      )}
                      <button
                        style={s.btn("danger")}
                        onClick={() => setConfirmDelete(look._id)}
                        disabled={isBusy}
                        title="Delete"
                      >
                        <FaTrash size={11} />
                      </button>
                      {isSanctionedLook(look) &&
                        (!look.appeal?.status ||
                          look.appeal.status === "none" ||
                          look.appeal.status === "denied") && (
                          <button
                            style={s.btn()}
                            onClick={() => {
                              setAppealModal(look._id);
                              setAppealReason("");
                              setAppealError("");
                            }}
                            title="Appeal this sanction"
                          >
                            <FaGavel size={11} /> Appeal
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* My Appeals panel */}
        {showAppeals && (
          <div
            style={{
              marginTop: "28px",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              background: "#fff",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "14px 16px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <strong style={{ fontSize: "15px" }}>My Appeals</strong>
              <button
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#6b7280",
                  fontSize: "18px",
                }}
                onClick={() => setShowAppeals(false)}
              >
                <FaTimes />
              </button>
            </div>
            {appealsLoading && (
              <div
                style={{ padding: "20px", color: "#6b7280", fontSize: "13px" }}
              >
                Loading…
              </div>
            )}
            {!appealsLoading && myAppeals.length === 0 && (
              <div
                style={{ padding: "20px", color: "#9ca3af", fontSize: "13px" }}
              >
                No appeals submitted yet.
              </div>
            )}
            {!appealsLoading &&
              myAppeals.map((look) => {
                const ap = look.appeal || {};
                const apColors = APPEAL_STATUS_COLORS[ap.status] || {};
                return (
                  <div
                    key={look._id}
                    style={{
                      padding: "14px 16px",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span style={{ fontWeight: "600", fontSize: "13px" }}>
                        {look.bookingSnapshot?.serviceName || "Service"}
                      </span>
                      {apColors.label && (
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: "600",
                            background: apColors.bg,
                            color: apColors.color,
                          }}
                        >
                          {apColors.label}
                        </span>
                      )}
                    </div>
                    {ap.reason && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginTop: "4px",
                        }}
                      >
                        Your reason: {ap.reason}
                      </div>
                    )}
                    {ap.adminResponse && (
                      <div
                        style={{
                          fontSize: "12px",
                          marginTop: "4px",
                          padding: "6px 10px",
                          background:
                            ap.status === "approved" ? "#f0fdf4" : "#fef2f2",
                          borderRadius: "6px",
                          color:
                            ap.status === "approved" ? "#166534" : "#991b1b",
                        }}
                      >
                        Admin: {ap.adminResponse}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginTop: "4px",
                      }}
                    >
                      Submitted:{" "}
                      {ap.submittedAt
                        ? new Date(ap.submittedAt).toLocaleString()
                        : "—"}
                      {ap.respondedAt
                        ? ` · Responded: ${new Date(ap.respondedAt).toLocaleString()}`
                        : ""}
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Policy panel */}
        <div
          style={{
            marginTop: "32px",
            padding: "14px 16px",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            fontSize: "12px",
            color: "#6b7280",
          }}
        >
          <strong style={{ color: "#374151" }}>Looks Policy Reminder</strong>
          <ul
            style={{
              margin: "6px 0 0",
              paddingLeft: "18px",
              lineHeight: "1.8",
            }}
          >
            <li>All looks require customer consent before publishing.</li>
            <li>
              You are responsible for obtaining valid consent from your clients.
            </li>
            <li>Reported looks are auto-hidden pending platform review.</li>
            <li>
              Repeated violations may result in restricted or suspended access
              to Looks.
            </li>
          </ul>
        </div>

        {/* Appeal modal */}
        {appealModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setAppealModal(null)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "10px",
                padding: "24px",
                maxWidth: "440px",
                width: "90%",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ marginTop: 0, fontSize: "18px" }}>
                Appeal Sanction
              </h2>
              <p style={{ color: "#6b7280", fontSize: "13px" }}>
                Explain why you believe this sanction should be reviewed. Be
                specific and professional.
              </p>
              {appealError && (
                <div
                  style={{
                    padding: "8px 12px",
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "6px",
                    color: "#991b1b",
                    fontSize: "13px",
                    marginBottom: "10px",
                  }}
                >
                  {appealError}
                </div>
              )}
              <textarea
                value={appealReason}
                onChange={(e) => setAppealReason(e.target.value)}
                placeholder="Describe why this sanction should be reversed…"
                rows={4}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  fontSize: "13px",
                  resize: "vertical",
                  boxSizing: "border-box",
                  marginBottom: "14px",
                }}
              />
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  style={{
                    padding: "8px 14px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                  onClick={() => setAppealModal(null)}
                >
                  Cancel
                </button>
                <button
                  style={{
                    padding: "8px 14px",
                    borderRadius: "6px",
                    border: "1px solid #1a1a1a",
                    background: "#1a1a1a",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                  disabled={!appealReason.trim() || appealBusy}
                  onClick={submitAppeal}
                >
                  {appealBusy ? "Submitting…" : "Submit Appeal"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirm modal */}
        {confirmDelete && (
          <div style={s.modal} onClick={() => setConfirmDelete(null)}>
            <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
              <h2 style={{ marginTop: 0, fontSize: "18px" }}>Delete Look?</h2>
              <p style={{ color: "#6b7280" }}>
                This look will be removed from your shop and profile. This
                cannot be undone.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}
              >
                <button style={s.btn()} onClick={() => setConfirmDelete(null)}>
                  Cancel
                </button>
                <button
                  style={s.btn("danger")}
                  onClick={() => deleteLook(confirmDelete)}
                  disabled={busyId === confirmDelete}
                >
                  {busyId === confirmDelete ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Look detail modal */}
        {selectedLook && (
          <div style={s.detailModal} onClick={() => setSelectedLook(null)}>
            <div style={s.detailBox} onClick={(e) => e.stopPropagation()}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "16px",
                }}
              >
                <h2 style={{ margin: 0, fontSize: "18px" }}>Look Details</h2>
                <button
                  style={{ ...s.btn(), padding: "4px 10px" }}
                  onClick={() => setSelectedLook(null)}
                >
                  Close
                </button>
              </div>

              {selectedLook.images?.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginBottom: "16px",
                  }}
                >
                  {selectedLook.images.map((img, i) => (
                    <div key={i} style={{ position: "relative" }}>
                      <img
                        src={img.url}
                        alt={`Look ${i + 1}`}
                        style={{
                          width: "130px",
                          height: "130px",
                          objectFit: "cover",
                          borderRadius: "6px",
                          display: "block",
                        }}
                      />
                      {i === 0 ? (
                        <span
                          title="Main image"
                          style={{
                            position: "absolute",
                            top: "4px",
                            left: "4px",
                            background: "rgba(202,138,4,0.95)",
                            color: "#fff",
                            fontSize: "11px",
                            fontWeight: "700",
                            padding: "2px 7px",
                            borderRadius: "999px",
                          }}
                        >
                          ★ Main
                        </span>
                      ) : (
                        <button
                          onClick={() => makeMainImage(selectedLook._id, i)}
                          disabled={busyId === selectedLook._id}
                          title="Make this the main image"
                          aria-label={`Make look image ${i + 1} the main image`}
                          style={{
                            position: "absolute",
                            top: "4px",
                            left: "4px",
                            background: "rgba(15,23,42,0.8)",
                            color: "#fbbf24",
                            border: "none",
                            fontSize: "16px",
                            lineHeight: "1",
                            padding: "4px 7px",
                            borderRadius: "999px",
                            cursor:
                              busyId === selectedLook._id ? "wait" : "pointer",
                          }}
                        >
                          ★
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {(selectedLook.images?.length || 0) > 1 && (
                <p
                  style={{
                    fontSize: "11px",
                    color: "#6b7280",
                    margin: "-8px 0 16px 0",
                  }}
                >
                  💡 Tip: the <span style={{ fontWeight: 700 }}>★</span> marks
                  the main/cover image — click another photo&apos;s ★ to make it
                  the main image.
                </p>
              )}

              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                }}
              >
                <tbody>
                  {[
                    [
                      "Status",
                      <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                        <span style={s.statusBadge(selectedLook.status)}>
                          {STATUS_LABELS[selectedLook.status]?.label ||
                            selectedLook.status}
                        </span>
                        {getModerationBadge(selectedLook) && (
                          <span
                            style={{
                              ...s.statusBadge("reported"),
                              background: `${getModerationBadge(selectedLook).color}22`,
                              color: getModerationBadge(selectedLook).color,
                            }}
                          >
                            {getModerationBadge(selectedLook).label}
                          </span>
                        )}
                      </div>,
                    ],
                    [
                      "Consent",
                      `${CONSENT_LABELS[selectedLook.consent?.status]?.label || selectedLook.consent?.status}`,
                    ],
                    [
                      "Service",
                      selectedLook.bookingSnapshot?.serviceName || "—",
                    ],
                    [
                      "Date",
                      selectedLook.bookingSnapshot?.appointmentDate || "—",
                    ],
                    [
                      "Visibility",
                      `Shop: ${selectedLook.visibility?.showOnShop ? "✓" : "✗"}  Profile: ${selectedLook.visibility?.showOnProfessional ? "✓" : "✗"}  Public: ${selectedLook.visibility?.isPublic ? "✓" : "✗"}`,
                    ],
                    ["Images", selectedLook.images?.length || 0],
                    [
                      "Created",
                      selectedLook.createdAt
                        ? new Date(selectedLook.createdAt).toLocaleDateString()
                        : "—",
                    ],
                  ].map(([label, value]) => (
                    <tr key={label}>
                      <td
                        style={{
                          padding: "6px 0",
                          color: "#6b7280",
                          width: "120px",
                          verticalAlign: "top",
                        }}
                      >
                        {label}
                      </td>
                      <td style={{ padding: "6px 0" }}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {selectedLook.auditTrail?.length > 0 && (
                <div style={{ marginTop: "20px" }}>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "14px",
                      marginBottom: "10px",
                    }}
                  >
                    Activity Timeline
                  </div>
                  <div
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      borderLeft: "2px solid #e5e7eb",
                      paddingLeft: "14px",
                    }}
                  >
                    {[...selectedLook.auditTrail].reverse().map((entry, i) => (
                      <div
                        key={i}
                        style={{ marginBottom: "10px", position: "relative" }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            left: "-19px",
                            top: "5px",
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: "#6b7280",
                          }}
                        />
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#374151",
                          }}
                        >
                          {entry.event.replace(/_/g, " ")}
                        </div>
                        <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                          {entry.createdAt
                            ? new Date(entry.createdAt).toLocaleString()
                            : ""}
                          {entry.actorRole ? ` · ${entry.actorRole}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default MyLooksPage;
